import * as React from 'react';
import Axios from 'axios';
import { ListingWithReps, OfferSettingsJsonType } from '@bb/domain/dist/types';
import {
  detectMobileDevice,
  isBrokerBayAdminEmail,
  isAssumeOrgType,
  isLimitedAccessOnNonActiveBrokerage,
} from 'utils';
import Loading from '@app/react-common/Loading';
import PanelHeader from '@app/react-common/PanelHeader';
import PanelBody from '@app/react-common/PanelBody';
import { RootScopeType } from '@app/caferealtor/Default.controller';
import { Button, Tooltip } from 'components';
import OfferListTableContainer from './OfferListTableContainer';

export interface OfferListProps {
  showStatus?: boolean;
  showActions?: boolean;
  listingId?: string;
  listing?: ListingWithReps & { isUnclaimed: boolean };
  $rootScope: RootScopeType;
  ModalService: any;
  $routeParams: {
    showForm801Modal?: string;
    manageNotifications?: string;
    offerId?: string;
  };
  ToastService: any;
  $scope: any;
  $location: any;
  ErrorService: any;
}

interface OfferListState {
  listing: ListingWithReps & { isUnclaimed: boolean };
  loading: boolean;
  mobileView?: boolean;
  offerSettings: OfferSettingsJsonType;
}

class OfferList extends React.Component<OfferListProps, OfferListState> {
  constructor(props: OfferListProps) {
    super(props);
    this.state = {
      listing: props.listing,
      loading: true,
      offerSettings: {} as OfferSettingsJsonType,
      mobileView: false,
    };
  }

  public async componentDidMount() {
    await this.fetchData();
    this.setState({ ...this.state, mobileView: detectMobileDevice() });
  }

  public fetchData = async () => {
    const [listing, offerSettings] = await Promise.all([
      this.fetchListing(),
      this.fetchOfferSettings(),
    ]);
    this.setState({
      listing,
      loading: false,
      offerSettings,
    });
  };

  private fetchListing = async () => {
    const { listing, listingId } = this.props;
    if (listing) {
      return listing;
    }
    const { data } = await Axios.get<
      ListingWithReps & { isUnclaimed: boolean }
    >(`/listing/${listingId}`);
    return data;
  };

  public fetchOfferSettings = async () => {
    const { listingId, $scope, $location, ErrorService } = this.props;
    try {
      const { data } = await Axios.get<OfferSettingsJsonType>(
        `/listing/${listingId}/offer_settings`,
      );
      return data;
    } catch (err) {
      $scope.$apply(() => {
        const { data } = err.response;
        $location.path(`/listing/${listingId}/view`);
        if (data.type === 'FORBIDDEN_ACTION') {
          data.message =
            'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.';
        }
        ErrorService.toastyHTTPErr(err.response);
      });
    }
  };

  private offerSettings = () => {
    const { listingId } = this.props;
    return (
      <Button
        key="offer-settings"
        className="btn btn-primary"
        href={`#/listing/${listingId}/offer_settings`}
      >
        Offer Settings
      </Button>
    );
  };

  private registerOffer = () => {
    const { $rootScope, listingId } = this.props;
    const { auth, orgStatus, currentPlan } = $rootScope;
    const { offerSettings, mobileView } = this.state;

    const isLimitedAccessOnNAB = isLimitedAccessOnNonActiveBrokerage(
      auth,
      orgStatus,
      currentPlan,
    );

    return (
      <Tooltip
        title={
          isLimitedAccessOnNAB &&
          'You cannot Register an Offer as this listing is not represented by BrokerBay.'
        }
      >
        <div>
          <Button
            key="register-offer"
            className={`btn btn-success`}
            style={{ margin: mobileView ? '0.5em 0em' : '0em 0.5em' }}
            href={`#/listing/${listingId}/offer/new`}
            disabled={
              !offerSettings.registration?.isEnabled || isLimitedAccessOnNAB
            }
            data-cy="offerlist-register-offer"
          >
            Register Offer
          </Button>
        </div>
      </Tooltip>
    );
  };

  private previewButton = () => {
    const { $rootScope, ModalService } = this.props;
    const { auth, orgStatus, currentPlan } = $rootScope;
    const { listing, mobileView } = this.state;

    const isLimitedAccessOnNAB = isLimitedAccessOnNonActiveBrokerage(
      auth,
      orgStatus,
      currentPlan,
    );

    const onClick = () => {
      $rootScope.$evalAsync(() => {
        ModalService.openPreviewModal(listing);
      });
    };

    return (
      <Tooltip
        title={
          isLimitedAccessOnNAB &&
          'You cannot view this modal as this listing is not represented by BrokerBay.'
        }
      >
        <div>
          <Button
            key="preview-button"
            onClick={onClick}
            className="btn btn-default"
            style={{ margin: mobileView ? '0.5em 0em' : '0em 0.5em' }}
            disabled={isLimitedAccessOnNAB}
            data-cy="offer-eyeball-preview"
          >
            <i className="pe-7s-look fat" />
          </Button>
        </div>
      </Tooltip>
    );
  };

  public render() {
    const { loading, listing, mobileView } = this.state;
    const {
      $rootScope,
      ModalService,
      showActions,
      showStatus,
      $routeParams,
      ToastService,
      $scope,
    } = this.props;

    const offerSettings = this.offerSettings();
    const registerOffer = this.registerOffer();
    const previewButton = this.previewButton();

    return loading ? (
      <Loading size="fa-5x" />
    ) : (
      <div>
        <PanelHeader
          title={
            <>
              <a
                className="listing-link"
                href={`#/listing/${listing.id}/view`}
              >{`${listing.line1} ${listing.line2 || ''}`}</a>
              <span> - Offers </span>
            </>
          }
          extra={[
            <div
              key="offer-action-buttons"
              style={{
                display: 'flex',
                flexDirection: mobileView ? 'column' : 'row',
                alignItems: mobileView && 'baseline',
              }}
            >
              {previewButton}
              {offerSettings}
              {listing && !listing.isDeleted && registerOffer}
            </div>,
          ]}
        />
        <PanelBody>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
            }}
          >
            <OfferListTableContainer
              listing={listing}
              ModalService={ModalService}
              $rootScope={$rootScope}
              showActions={showActions}
              showStatus={showStatus}
              $routeParams={$routeParams}
              ToastService={ToastService}
              $scope={$scope}
            />
          </div>
        </PanelBody>
      </div>
    );
  }
}

export default OfferList;
